import { useEffect, useState, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import {
  ArrowLeft,
  GitBranch,
  House,
} from '@phosphor-icons/react';
import { useApi } from '../../hooks/useApi';
import { useToast } from '../../context/ToastContext';
import { useLanguage } from '../../context/LanguageContext';
import FamilyTree from '../tree/FamilyTree';
import { parseHybridSlug } from '../../utils/treeUrl';



export default function BranchView() {
  const { branchSlug } = useParams();
  const branchId = parseHybridSlug(branchSlug);
  const api = useApi();
  const { addToast } = useToast();
  const { t } = useLanguage();

  const [branch, setBranch] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    setBranch(null);

    const [branchData, branchErr] = await api.get(`/branches/${branchId}`);
    if (branchErr) {
      setError(typeof branchErr === 'string' ? branchErr : t('branchView.loadError'));
      addToast(t('branchView.loadError'), 'error');
      setLoading(false);
      return;
    }

    setBranch(branchData);
    setLoading(false);
  }, [api, branchId, addToast, t]);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <div className="flex flex-col h-[calc(100vh-64px)] bg-ft-bg">
      <div className="shrink-0 border-b border-slate-200 bg-white/80 backdrop-blur-sm px-4 md:px-6 py-3">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div>
            <div className="flex items-center gap-2 text-xs text-ft-text-3 mb-0.5">
              <House className="text-ft-accent" weight="fill" />
              <span>{branch?.family?.name || t('common.family')}</span>
              <span>/</span>
              <GitBranch className="text-ft-accent" />
              <span>{t('common.branches')}</span>
            </div>
            <h1 className="text-lg font-extrabold text-ft-text-1 tracking-tight leading-tight">
              {branch?.name || t('branchView.title')}
            </h1>
            {branch?.description && (
              <p className="text-xs text-ft-text-3 mt-0.5">{branch.description}</p>
            )}
          </div>
          <Link
            to="/families"
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-ft-header text-white text-xs font-bold no-underline hover:bg-ft-header/90 transition-colors"
          >
            <ArrowLeft /> {t('branchView.backToFamilies')}
          </Link>
        </div>
      </div>

      <div className="flex-1 min-h-0 relative">
        {loading ? (
          <div className="flex flex-col items-center justify-center h-full gap-4 text-ft-text-2">
            <div className="w-10 h-10 rounded-full border-[3px] border-slate-200 border-t-ft-accent animate-spin" />
            <p className="text-sm">{t('branchView.loading')}</p>
          </div>
        ) : error ? (
          <div className="flex items-center justify-center h-full">
            <div className="bg-red-50 text-red-700 border border-red-200 rounded-2xl p-4 text-sm font-semibold">
              {error}
            </div>
          </div>
        ) : (
          <FamilyTree branchId={Number(branchId)} className="!h-full" />
        )}
      </div>
    </div>
  );
}
